/**
 * Telemetry for stale-chunk recovery (SD-10509, final part of SD-10436).
 *
 * Every recovery attempt — successful reload or exhausted throttle — emits a
 * PostHog `chunk_load_recovery` event from the router error boundaries.
 * Successful recovery used to be intentionally silent, which made the
 * "Updating…" symptom untraceable; this measures its frequency so the deploy
 * fixes (SD-10437 retention, SD-10488 version check, SD-10507 SW removal) are
 * verifiable in production.
 *
 * Property values are deliberately low-cardinality: route paths are
 * normalized (ids stripped) and error messages are mapped to a small category
 * set. Never include raw URLs or query strings.
 */

export const CHUNK_RECOVERY_EVENT = "chunk_load_recovery";

type ChunkRecoveryBoundary = "root" | "auth" | "route";
type ChunkRecoveryAction = "reload" | "exhausted";

/** Maps a chunk-load error message onto the matcher that classified it. */
export const categorizeChunkError = (message: string): string => {
  const normalized = message.toLowerCase();
  if (normalized.includes("failed to fetch dynamically imported module")) {
    return "fetch-dynamic-import";
  }
  if (normalized.includes("error loading dynamically imported module")) {
    return "load-dynamic-import";
  }
  if (normalized.includes("importing a module script failed")) {
    return "import-module-script";
  }
  if (normalized.includes("failed to load module script")) {
    return "load-module-script";
  }
  if (normalized.includes("mime type") && normalized.includes("module")) {
    return "module-mime-type";
  }
  return "other";
};

// Path segments that are entity ids, not route structure: Mongo ObjectIds,
// UUIDs, and purely numeric segments.
const ID_SEGMENT =
  /^([0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

/** `/conversations/666f8d88…/notes` → `/conversations/:id/notes`. */
export const normalizeRoutePath = (pathname: string): string =>
  pathname
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");

// `type` (not `interface`) so it structurally satisfies the
// Record<string, unknown> that captureClientRouteError takes as `extra`.
type ChunkRecoveryProps = {
  boundary: ChunkRecoveryBoundary;
  action: ChunkRecoveryAction;
  errorCategory: string;
  routePath: string;
  buildVersion: string;
  visibilityState: string;
  hasServiceWorkerController: boolean;
};

/**
 * Collects the event payload from browser state. Only called from error
 * boundaries (browser context); each read is defensive anyway so a partial
 * environment can never turn a recovery path into a second crash.
 */
export const collectChunkRecoveryProps = (
  boundary: ChunkRecoveryBoundary,
  action: ChunkRecoveryAction,
  errorMessage: string,
): ChunkRecoveryProps => {
  let routePath = "unknown";
  try {
    routePath = normalizeRoutePath(window.location.pathname);
  } catch {
    // Keep "unknown".
  }
  let visibilityState = "unknown";
  try {
    visibilityState = document.visibilityState;
  } catch {
    // Keep "unknown".
  }
  let hasServiceWorkerController = false;
  try {
    // Observes the SD-10507 tombstone drain: should trend to zero.
    hasServiceWorkerController = !!navigator.serviceWorker?.controller;
  } catch {
    // Keep false.
  }
  return {
    boundary,
    action,
    errorCategory: categorizeChunkError(errorMessage),
    routePath,
    buildVersion: import.meta.env.VITE_BUILD_VERSION ?? "",
    visibilityState,
    hasServiceWorkerController,
  };
};
